import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, Grid2, IconButton, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { useGetSalesTaxesListQuery } from 'api/hooks/billing/tax';
import { SaveButton } from 'app/components';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

interface SplitOrChangeTaxDialogProps {
  open: boolean;
  handleClose: () => void;
  setSplitArticle: (split: boolean) => void;
  setSplitOrChangeTaxDialogOpen: (split: boolean) => void;
  updateOrCreateArticle: () => Promise<void>;
  isSubmitting: boolean;
  salesTaxVersionId?: string;
}

export const SplitOrChangeTaxDialog = ({
  open,
  handleClose,
  setSplitArticle,
  setSplitOrChangeTaxDialogOpen,
  updateOrCreateArticle,
  isSubmitting,
  salesTaxVersionId
}: SplitOrChangeTaxDialogProps) => {
  const { t } = useTranslation();
  const { data: salesTaxes } = useGetSalesTaxesListQuery();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const salesTax = salesTaxes?.data.find(tax => tax.id === salesTaxVersionId);

  //Only use in the SplitOrChangeTaxDialog with the split Button. Splits invoice in two with same taxes
  const handleSplit = async () => {
    try {
      setSplitArticle(true);
      await updateOrCreateArticle();
      dispatch(
        AlertActions.success(t(translations.pages.billingFinance.mutation.splitInvoiceSuccessful()))
      );
      navigate('/billing/finance');
    } catch {
      dispatch(
        AlertActions.error(t(translations.pages.billingFinance.mutation.splitInvoiceFailed()))
      );
    } finally {
      setSplitOrChangeTaxDialogOpen(false);
    }
  };

  //Only use in the SplitOrChangeTaxDialog with the EqualiseAll Button. Updates all Taxes to then new one in invoice
  const handleEqualiseTaxRateInInvoice = async () => {
    try {
      setSplitArticle(false);
      await updateOrCreateArticle();
      dispatch(
        AlertActions.success(
          t(translations.pages.billingFinance.mutation.equaliseTaxRateSuccesful())
        )
      );
    } catch {
      dispatch(
        AlertActions.error(t(translations.pages.billingFinance.mutation.equaliseTaxRateFailed()))
      );
    } finally {
      setSplitOrChangeTaxDialogOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose}>
        <Grid2
          container
          margin={1.5}
          borderBottom={1}
          borderColor={'lightgray'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t(translations.pages.billingFinance.splitOrChangeTaxDialog.title())}
          </Typography>
          <IconButton aria-label="close" onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Grid2>
        <DialogContent>
          <Typography variant="subtitle1">
            {t(translations.pages.billingFinance.splitOrChangeTaxDialog.descripiton())}
          </Typography>
          <br />
          <Typography variant="body1">
            <b>
              {`${t(
                translations.pages.billingFinance.splitOrChangeTaxDialog.splitInvoiceButton()
              )}: `}
            </b>
            {t(translations.pages.billingFinance.splitOrChangeTaxDialog.splitInvoice())}
          </Typography>
          <br />
          <Typography variant="body1">
            <b>
              {`${t(
                translations.pages.billingFinance.splitOrChangeTaxDialog.equaliseTaxRateButton()
              )}: `}
            </b>
            {t(translations.pages.billingFinance.splitOrChangeTaxDialog.equaliseTaxRate(), {
              taxToChange: `${salesTax?.text}`
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <SaveButton
            onClick={handleEqualiseTaxRateInInvoice}
            color="primary"
            variant="contained"
            isSubmitting={isSubmitting}
          >
            {t(translations.pages.billingFinance.splitOrChangeTaxDialog.equaliseTaxRateButton())}
          </SaveButton>
          <SaveButton
            onClick={handleSplit}
            color="primary"
            variant="contained"
            isSubmitting={isSubmitting}
          >
            {t(translations.pages.billingFinance.splitOrChangeTaxDialog.splitInvoiceButton())}
          </SaveButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
